import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { type TableColumn, safeAbsurd } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ColumnResizeHandle } from 'components/Table/components/ColumnResizeHandle';
import { TableStickyContainer } from 'components/Table/components/TableStickyContainer';
import { getCellWidthAndPaddingStyle } from 'components/Table/helpers/getCellWidthAndPaddingStyle';
import { getColumnDefaultWidth } from 'components/Table/helpers/getColumnWidth';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { selectRenderedColumnWidths } from 'components/Table/model/selectors/selectRenderedColumnWidths';
import { selectVisibleColumnsByStickiness } from 'components/Table/model/selectors/selectVisibleColumnsByStickiness';
import { pipe } from 'fp-ts/lib/function';
import type { ComponentPropsWithoutRef, FC } from 'react';
import { SimpleFilter } from './SimpleFilter/SimpleFilter';

/**
 * Don't render intent filter & sorting icon beyond this width as they get glitchy
 */
const MIN_WIDTH_FOR_TITLE_CONTROLS = 50;

// custom class name is used there as it is grabbed by javascript autosizing tools
const cellTitleClass =
  'ipf-table-header-cell__title font-semibold text-xs text-plain';

type Props = {
  disableSticky?: boolean;
};

export const TableHeader: FC<Props> = (props) => {
  const ctxProps = useTableContextSelectors({
    renderedColumnWidths: selectRenderedColumnWidths,
    visibleColumnsByStickiness: selectVisibleColumnsByStickiness,
    onChangeFilter: prop('onChangeFilter'),
    onChangeIntentFilter: prop('onChangeIntentFilter'),
    onSubmitFilters: prop('onSubmitFilters'),
    sort: prop('sort'),
    onSortChange: prop('onSortChange'),
    showOnlyIntentResultsOnColumns: prop('showOnlyIntentResultsOnColumns'),
    simpleFilterFields: prop('simpleFilterFields'),
    reports: prop('reports'),
  });

  const getSortIconProps = (
    columnKey: string,
  ): ComponentPropsWithoutRef<typeof Icon> => {
    if (ctxProps.sort?.column === columnKey) {
      switch (ctxProps.sort.order) {
        case 'asc':
          return { glyph: 'fa-sort-up', iconStyle: 'solid' };
        case 'desc':
          return { glyph: 'fa-sort-down', iconStyle: 'solid' };
        default:
          safeAbsurd(ctxProps.sort.order);
      }
    }

    return { glyph: 'fa-sort', iconStyle: 'solid', className: 'text-gray-600' };
  };

  const renderColumnTitle = (
    column: TableColumn<any>,
    allowSortIcon: boolean,
  ) => {
    const sortIcon =
      !column.disabledSort &&
      allowSortIcon &&
      pipe(column.key, getSortIconProps, ({ className, ...otherProps }) => (
        <Icon
          className={classNames('flex-fixed', className)}
          size='xs'
          {...otherProps}
        />
      ));

    const title = (() => {
      if (column.customFrontendTitle) {
        return column.customFrontendTitle;
      }
      if (column.description) {
        return (
          <Tooltip content={column.description}>
            <div className={classNames(cellTitleClass, 'truncate')}>
              {column.title}
            </div>
          </Tooltip>
        );
      }
      return (
        <TextOverflowTooltip className={cellTitleClass}>
          {column.title}
        </TextOverflowTooltip>
      );
    })();

    if (!column.disabledSort && ctxProps.onSortChange) {
      // A: if sort handler is available, render title with sorting control
      return (
        <div
          className='flex min-w-0 gap-1 flex-1 cursor-pointer flex-row justify-between'
          onClick={() => {
            ctxProps.onSortChange!({
              column: column.key,
              order:
                ctxProps.sort?.column === column.key &&
                ctxProps.sort.order === 'desc'
                  ? 'asc'
                  : 'desc',
            });
          }}
        >
          {title}
          {sortIcon}
        </div>
      );
    }
    if (ctxProps.sort?.column === column.key) {
      // B: if no change handler is provided but table is sorted by this column,
      // render it as non-clickable
      return (
        <>
          {title}
          {sortIcon}
        </>
      );
    }
    // C: no sort handler & not sorted by this column
    return title;
  };

  const renderCell = (sticky: boolean) => (column: TableColumn<any>) => {
    const style = pipe(
      ctxProps.renderedColumnWidths[column.key] ??
        getColumnDefaultWidth(column),
      getCellWidthAndPaddingStyle(),
    );
    const allowTitleControls = style.width >= MIN_WIDTH_FOR_TITLE_CONTROLS;
    const allowIntentFilter =
      (ctxProps.showOnlyIntentResultsOnColumns === undefined ||
        ctxProps.showOnlyIntentResultsOnColumns.includes(column.key)) &&
      allowTitleControls &&
      ctxProps.reports?.[column.key] !== undefined &&
      ctxProps.onChangeIntentFilter !== undefined;
    const filter = ctxProps.simpleFilterFields?.[column.key];

    return (
      <div
        className='ipf-table-header-cell'
        data-column-header={column.key}
        key={column.key}
        style={style}
      >
        <div className='ipf-table-header-cell__content'>
          {renderColumnTitle(column, allowTitleControls)}
        </div>
        {allowTitleControls &&
          filter &&
          ctxProps.onChangeFilter &&
          ctxProps.onSubmitFilters && (
            <SimpleFilter
              filterField={filter}
              onChangeFilter={ctxProps.onChangeFilter}
              onSubmitFilters={ctxProps.onSubmitFilters}
              columnKey={column.key}
              allowIntentFilter={allowIntentFilter}
              columnWidth={
                ctxProps.renderedColumnWidths[column.key] ??
                getColumnDefaultWidth(column)
              }
            />
          )}
        {!column.fixedWidth && (
          <ColumnResizeHandle
            columnKey={column.key}
            width={style.width}
            sticky={sticky}
          />
        )}
      </div>
    );
  };

  return (
    <div className='ipf-table__header'>
      {!props.disableSticky &&
        ctxProps.visibleColumnsByStickiness.sticky.length > 0 && (
          <TableStickyContainer>
            {ctxProps.visibleColumnsByStickiness.sticky.map(renderCell(true))}
          </TableStickyContainer>
        )}
      {props.disableSticky &&
        ctxProps.visibleColumnsByStickiness.sticky.map(renderCell(false))}
      {ctxProps.visibleColumnsByStickiness.scrolling.map(renderCell(false))}
    </div>
  );
};
